import { PersonDemographicTypes, ContinueType, TextContentType } from '@myriadgenetics/mgh-types';
import axios from 'axios';
import type { AxiosResponse } from 'axios';
import { AuthOrderData, AuthOrderDataResponse, MghOrderData } from '../survey-container/types';
import { ENDPOINT_URLS } from '../../helpers/api';
import { WrappedUseHistory } from '../../helpers/routing';
import { isValidUUID } from '../survey-container/utils';
import Logger from '../../helpers/logger';

export const doesPatientOrderExist = async (responses: any, history: WrappedUseHistory, setErrorMessage: (value: string) => void) => {
  const mghOrderDataToFetch: AuthOrderData = {
    firstName: responses.authPage.patient.firstName,
    lastName: responses.authPage.patient.lastName,
    dob: responses.authPage.patient.dob,
    phone: responses.authPage.patient.phone,
  };
  const orderData: AuthOrderDataResponse | null = await getMghOrderData(mghOrderDataToFetch);
  const patientOrderData = orderData?.uuid ? await getPatientOrderData(orderData.uuid) : null;
  const hasDetails500Error = Boolean(patientOrderData && 'status' in patientOrderData && patientOrderData.status === 500);

  if (patientOrderData && 'patient' in patientOrderData && orderData?.url) {
    const mghOrderUrl = orderData.url;
    history.push(`/${mghOrderUrl}/lang`, {
      mghOrderData: patientOrderData,
      locResponses: {
        mghOrderUuid: patientOrderData.uuid,
        mghOrderData: patientOrderData,
      },
    });
  } else if ((orderData !== null && orderData.status === 500) || hasDetails500Error) {
    setErrorMessage('auth.error500Message');
  } else {
    setErrorMessage('auth.errorMessage');
  }
};

export const getMghOrderData = async (patientData: AuthOrderData) => {
  const configBaseUrl = window?.mghConfig?.baseURL;
  try {
    const { data }: AxiosResponse<AuthOrderDataResponse> = await axios.post(`${configBaseUrl}${ENDPOINT_URLS.mghOrderAuthentications}`, patientData);
    return data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return { error: error.response.data, status: error.response.status };
    }
    throw error;
  }
};

export const getPatientOrderData = async (mghOrderId: string) => {
  const configBaseUrl = window?.mghConfig?.baseURL;

  if (!isValidUUID(mghOrderId)) {
    Logger.logError(`Invalid mghOrderId format: ${mghOrderId}`);
    throw new Error('Invalid order ID format - must be a valid UUID');
  }

  if (!configBaseUrl) {
    Logger.logError('Base URL not configured');
    throw new Error('API base URL not configured');
  }

  try {
    const { data }: AxiosResponse<MghOrderData> = await axios.post(`${configBaseUrl}${ENDPOINT_URLS.mghOrderDetails}`, { uuid: mghOrderId });
    return data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return { error: error.response.data, status: error.response.status };
    }
    throw error;
  }
};

export const authNode = {
  id: -1,
  name: 'Authentication Page',
  description: 'Page for user authentication',
  type: 'PAGE',
  data: {
    continueKey: 'auth.confirm',
    continueType: ContinueType.End,
  },
  components: [
    {
      id: -7,
      type: 'PERSON_DEMOGRAPHIC',
      data: {
        questionKey: 'auth.confirmIdentity',
        descriptionKey: 'auth.confirmIdentitySubtitle',
        answerKey: 'patient',
        required: true,
        type: PersonDemographicTypes.Provider,
        fields: {
          firstName: 'REQUIRED',
          lastName: 'REQUIRED',
          dob: 'REQUIRED',
          phone: 'REQUIRED',
        },
      },
    },
    {
      id: -9,
      type: 'TEXT_CONTENT',
      name: 'Privacy Disclaimer',
      description: 'Disclaimer for auth page',
      data: {
        contentKey: 'auth.privacyDisclaimer',
        textLinks: [
          {
            urlKey: 'auth.links.hipaaLink',
            targetIsBlank: true,
            openInModal: true,
          },
          {
            urlKey: 'auth.links.privacyLink',
            targetIsBlank: true,
            openInModal: true,
          },
          {
            urlKey: 'auth.links.healthDataLink',
            targetIsBlank: true,
            openInModal: true,
          },
        ],
        inlineImages: [],
        type: TextContentType.Text2,
        showComponentsInDialog: false,
      },
      componentIds: [],
    },
  ],
};
